import type { WorkflowConfiguration, StageConfiguration, FieldConfiguration } from "@/types/workflow"

const CLIENT_REQUEST_FIELD_NAMES: Record<string, Record<string, string>> = {
  'RequestType': { en: 'Request Type', fr: 'Type de demande' },
  'Status': { en: 'Status', fr: 'Statut' },
  'RequestDetails': { en: 'Request Details', fr: 'Détails de la demande' },
  'WorkerFullName': { en: 'Worker', fr: 'Travailleur' },
  // 'Client' is the collapsed parent path produced by audit-history-panel's
  // collapseNestedDiffs for the structured Client object on ClientRequestDocument.
  // 'ClientOrganization' kept for back-compat with older audit rows that used the
  // legacy flat alias.
  'Client': { en: 'Client', fr: 'Client' },
  'ClientOrganization': { en: 'Client', fr: 'Client' },
  // 'AssignedToUser' is the collapsed parent path from collapseNestedDiffs.
  // 'AssignedToUserName' kept for back-compat with legacy flat-alias audit rows.
  'AssignedToUser': { en: 'Assigned To', fr: 'Assigné à' },
  'AssignedToUserName': { en: 'Assigned To', fr: 'Assigné à' },
  'StartDate': { en: 'Start Date', fr: 'Date de début' },
  'EndDate': { en: 'End Date', fr: 'Date de fin' },
  'BillRate': { en: 'Bill Rate', fr: 'Taux facturé' },
  'BillCurrencyCode': { en: 'Bill Currency', fr: 'Devise de facturation' },
  'BillRateTypeCode': { en: 'Bill Rate Unit', fr: 'Unité de taux facturé' },
  'TerminationDetails': { en: 'Termination Details', fr: 'Détails de la résiliation' },
  'JobTitle': { en: 'Job Title', fr: 'Titre du poste' },
  'HiringManagerName': { en: 'Hiring Manager', fr: "Gestionnaire d'embauche" },
  'RequestedByName': { en: 'Requested By', fr: 'Demandé par' },
  'ActionedByUserName': { en: 'Actioned By', fr: 'Action par' },
}

const CLIENT_REQUEST_STATUS_VALUES: Record<string, Record<string, string>> = {
  'RequestInitiated': { en: 'Request Initiated', fr: 'Demande initiée' },
  'RequestAccepted': { en: 'Acknowledged', fr: 'Acceptée' },
  'Canceled': { en: 'Canceled', fr: 'Annulée' },
  'OnHold': { en: 'On Hold', fr: 'En attente' },
  'VerifyingWorker': { en: 'Pre-Verification Requested', fr: 'Pré-vérification demandée' },
  'ReviewWorkerInfo': { en: 'Pre-Verification Review', fr: 'Révision de pré-vérification' },
  'WorkerInfoReviewed': { en: 'Pre-Verification Complete', fr: 'Pré-vérification terminée' },
  'Draft': { en: 'Draft', fr: 'Brouillon' },
}

const CLIENT_REQUEST_STATUS_NAMES: Record<string, Record<string, string>> = {
  'Onboarding': { en: 'Onboarding Request', fr: "Demande d'intégration" },
  'Extension': { en: 'Extension Request', fr: 'Demande de prolongation' },
  'Termination': { en: 'Termination Request', fr: 'Demande de résiliation' },
  'Redeployment': { en: 'Redeployment Request', fr: 'Demande de redéploiement' },
  'General': { en: 'General Request', fr: 'Demande générale' },
  'Other': { en: 'Other Request', fr: 'Autre demande' },
}

export interface ResolvedAuditData {
  stageTitle: string
  fieldDisplayName: string
  actionDisplayName: string
}

/**
 * Resolves the display name for a stage code from workflow configurations
 */
export function resolveStageTitle(
  stageCode: string,
  configuration: WorkflowConfiguration | undefined,
  language: 'en' | 'fr' = 'en',
  entityType?: string
): string {
  if (entityType === 'ClientRequest') {
    return CLIENT_REQUEST_STATUS_NAMES[stageCode]?.[language]
      ?? CLIENT_REQUEST_STATUS_NAMES[stageCode]?.['en'] ?? stageCode
  }

  if (!configuration?.stageConfigurations) {
    return stageCode
  }

  const stageConfig = configuration.stageConfigurations.find(
    stage => stage.stageCode === stageCode
  )

  if (!stageConfig) {
    return stageCode
  }

  const title = language === 'fr' ? stageConfig.titleFrench : stageConfig.titleEnglish
  return title
}

export function resolveClientRequestFieldValue(
  fieldName: string,
  value: string,
  language: 'en' | 'fr' = 'en'
): string | null {
  if (fieldName === 'Status') {
    return CLIENT_REQUEST_STATUS_VALUES[value]?.[language]
      ?? CLIENT_REQUEST_STATUS_VALUES[value]?.['en']
      ?? value
  }
  return null
}

/**
 * Resolves the display name for a field from workflow configurations
 * Returns null if the field is not found in the configuration (should be hidden)
 */
export function resolveFieldDisplayName(
  fieldName: string,
  configuration: WorkflowConfiguration | undefined,
  language: 'en' | 'fr' = 'en',
  propertyPath?: string,
  entityType?: string
): string | null {
  // Config-based resolution runs FIRST for every entity, including ClientRequest. The
  // unified request tabs (ClientRequest / VmsRequest / AtsPlacementRequest) all carry real
  // WHC field configs now, so audit labels come from the same fieldsInUse the grid columns
  // use — no per-entity hardcoded field list decides what renders. (The CLIENT_REQUEST_FIELD_NAMES
  // map is kept only as a legacy fallback below, for old audit rows that used flat aliases the
  // config no longer has.)
  if (configuration?.fieldsInUse) {
    // Nested-object diffs for an *added* object arrive with an array-index segment in the
    // path (e.g. "AssignedToUser/0" instead of "AssignedToUser") because the diff engine
    // models a null → object add as a single-element array. Strip "/<n>" segments so the
    // path resolves to the same columnCode ("AssignedToUser") as the modify case. Without
    // this, the leaf fields of a first-time-set nested object all resolve to null here, so
    // the display filters (displayableRecords / hasMeaningfulDifferences / search) treat
    // the record as having no valid changes and drop it from the History tab entirely.
    // Mirrors collapseNestedDiffs' normalizePath in audit-history-panel.tsx.
    const normalizedPath = propertyPath?.replace(/\/\d+(?=\/|$)/g, '')

    // If fieldName is just a number (array index), prioritize propertyPath matching.
    if (normalizedPath && /^\d+$/.test(fieldName)) {
      const byPath = configuration.fieldsInUse.find(
        field => field.columnCode && field.columnCode.toLowerCase() === normalizedPath.toLowerCase()
      )
      if (byPath) return language === 'fr' ? byPath.columnNameFrench : byPath.columnNameEnglish
    }

    // Match by exact columnCode (case insensitive), then by propertyPath.
    let fieldConfig = configuration.fieldsInUse.find(
      field => field.columnCode && field.columnCode.toLowerCase() === fieldName.toLowerCase()
    )
    if (!fieldConfig && normalizedPath) {
      fieldConfig = configuration.fieldsInUse.find(
        field => field.columnCode && field.columnCode.toLowerCase() === normalizedPath.toLowerCase()
      )
    }
    if (fieldConfig) {
      return language === 'fr' ? fieldConfig.columnNameFrench : fieldConfig.columnNameEnglish
    }
  }

  // Legacy fallback (ClientRequest only): curated labels for fields the config doesn't carry
  // — e.g. older audit rows written against flat aliases. Safety net, not the primary path.
  if (entityType === 'ClientRequest') {
    return CLIENT_REQUEST_FIELD_NAMES[fieldName]?.[language]
      ?? CLIENT_REQUEST_FIELD_NAMES[fieldName]?.['en'] ?? null
  }

  return null
}

/**
 * Resolves the display name for an action
 */
export function resolveActionDisplayName(action: string): string {
  const actionMap: Record<string, string> = {
    'Create': 'Created',
    'Update': 'Updated',
    'Edit': 'Edited',
    'Delete': 'Deleted',
    'StatusChange': 'Status Changed',
    'Assignment': 'Assigned',
    'Unassignment': 'Unassigned'
  }

  return actionMap[action] || action
}

/**
 * Formats a value for display in the audit history
 */
export function formatAuditValue(value: any): string {
  if (value === null || value === undefined) return '—'
  
  // Handle boolean values
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }
  
  // Handle numbers
  if (typeof value === 'number') {
    return value.toString()
  }
  
  // Handle strings
  if (typeof value === 'string') {
    if (value.trim() === '') return '—'
    if (value.toLowerCase() === 'true') return 'Yes'
    if (value.toLowerCase() === 'false') return 'No'
    return value
  }
  
  // Handle other types (objects, arrays, etc.)
  return String(value)
}

/**
 * Gets the appropriate icon for an action type
 */
export function getActionIcon(action: string): string {
  const actionIconMap: Record<string, string> = {
    'Create': 'Plus',
    'Update': 'Edit',
    'Delete': 'Trash2',
    'StatusChange': 'RefreshCw',
    'Assignment': 'UserPlus',
    'Unassignment': 'UserMinus'
  }

  return actionIconMap[action] || 'Edit'
}
